import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DefaultTheme, Div, Icon, Text } from '@pinpoint/design-kit';

import { Button } from 'src/components';

const HOST = 'sftp.onpoint.pinpoint.ai';

const FOLDERS = [
  ['actuarial/file.csv', 'Actuarial'],
  ['classifier/hits/file.csv', 'Classifier hits'],
  ['classifier/validation/file.csv', 'Classifier validation'],
  ['classifier/miss/file.csv', 'Classifier miss'],
  ['predict/file.csv', 'Predict'],
];

function exampleUser(username) {
  return username || 'client_user';
}

function CodeBlock({ children, output }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = () => {
    navigator.clipboard.writeText(children).then(() => setCopied(true));
  };

  return (
    <Div
      m={{ y: '0.75rem' }}
      rounded="sm"
      overflow="auto"
      style={{
        position: 'relative',
        background: '#111',
        padding: '0.85rem 1rem',
        paddingRight: output ? '1rem' : '2.25rem',
        color: '#f2f2f2',
        whiteSpace: 'pre-wrap',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: '0.85rem',
        lineHeight: '1.55',
      }}
    >
      {output ? null : (
        <button
          type="button"
          aria-label={copied ? 'Copied' : 'Copy command'}
          data-tip={copied ? 'Copied' : 'Copy'}
          onClick={copy}
          style={{
            position: 'absolute',
            top: '0.55rem',
            right: '0.55rem',
            background: 'transparent',
            border: 0,
            padding: 0,
            cursor: 'pointer',
          }}
        >
          <Icon
            name={copied ? 'Checked' : 'Copy'}
            color="white"
            viewBox={copied ? '0 0 50 50' : '0 0 14 14'}
            size=".9rem"
          />
        </button>
      )}
      {children.split('\n').map((line) => {
        const key = `${output ? 'out' : 'cmd'}:${line}`;
        return (
          <div key={key}>
            {output ? null : <span style={{ color: '#9be59b' }}>$ </span>}
            {line}
          </div>
        );
      })}
    </Div>
  );
}

CodeBlock.propTypes = {
  children: PropTypes.string.isRequired,
  output: PropTypes.bool,
};

CodeBlock.defaultProps = {
  output: false,
};

function Section({ title, children }) {
  return (
    <Div m={{ t: '1.5rem' }}>
      <Text tag="h2" textColor="navyBlue" textSize="subtitle" textWeight="500">
        {title}
      </Text>
      {children}
    </Div>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function ClientSftpDocs({ username }) {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const user = exampleUser(username);
  const { headerHeight } = DefaultTheme.application;

  useEffect(() => {
    if (!open) {
      setShown(false);
      return undefined;
    }
    const frame = requestAnimationFrame(() => setShown(true));
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <Button
        buttonStyle="primary"
        data-test="sftp-documentation"
        data-tip="View SFTP documentation"
        onClick={() => setOpen(true)}
      >
        <Icon name="ApiDocs" color="white" m={{ r: '0.5rem' }} viewBox="0 0 15 18" size="1rem" />
        DOCUMENTATION
      </Button>
      {open ? (
        <>
          <Div
            pos="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="black"
            opacity="0.2"
            zIndex="1000"
            cursor="pointer"
            onClick={() => setOpen(false)}
          />
          <Div
            role="dialog"
            aria-label="SFTP documentation"
            data-test="sftp-documentation-panel"
            pos="fixed"
            right="0"
            w={{ xs: '100vw', sm: '36rem' }}
            bg="white"
            zIndex="1001"
            overflow="auto"
            shadow="5"
            p={{ x: '1.5rem', y: '1.25rem' }}
            style={{
              top: headerHeight,
              height: `calc(100vh - ${headerHeight})`,
              transform: shown ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 240ms ease-in-out',
            }}
          >
            <Div d="flex" justify="space-between" align="center">
              <Button
                buttonStyle="secondary"
                rounded="circle"
                w="2.5rem"
                h="2.4rem"
                p="0rem"
                data-tip="Hide"
                aria-label="Hide documentation"
                onClick={() => setOpen(false)}
              >
                <Icon color="white" name="RightArrow" />
              </Button>
              <Text textColor="navyBlue" textSize="title">
                SFTP documentation
              </Text>
            </Div>
            <Text textSize="small" m={{ t: '0.75rem' }}>
              Create a key, send Pinpoint the public half, and upload each CSV into the folder that matches its file
              type.
            </Text>

            <Section title="Create an SSH key">
              <Text textSize="small" m={{ t: '0.5rem' }}>
                Run this on the computer that will connect. It creates a private key and a public key. Send only the
                public key.
              </Text>
              <CodeBlock>{`ssh-keygen -t ed25519 -f client_sftp -C "${user}"`}</CodeBlock>
              <Text textSize="small">The public key is the file ending in .pub. Show it with:</Text>
              <CodeBlock>cat client_sftp.pub</CodeBlock>
              <Text textSize="small">Paste that whole line into the Public key field. It looks like this:</Text>
              <CodeBlock output>{`ssh-ed25519 AAAA... ${user}`}</CodeBlock>
              <Text textSize="small">
                Keep client_sftp private. Do not paste it into OnPoint, and do not send it to Pinpoint.
              </Text>
            </Section>

            <Section title="Connect">
              <Text textSize="small" m={{ t: '0.5rem' }}>
                The connection has to come from one of the public IPs saved for this client. Use the username saved on
                this page.
              </Text>
              <CodeBlock>{`sftp -i client_sftp ${user}@${HOST}`}</CodeBlock>
              <Text textSize="small">After login, pwd is /. Upload a file with put, and list a folder with ls.</Text>
              <CodeBlock>{`put hits.csv classifier/hits/hits.csv\nls classifier/hits`}</CodeBlock>
            </Section>

            <Section title="Where to put each file">
              <Text textSize="small" m={{ t: '0.5rem' }}>
                The folder name sets the file type. A file outside these folders is not listed in OnPoint.
              </Text>
              {FOLDERS.map(([path, label]) => (
                <Div key={path} m={{ t: '0.75rem' }}>
                  <Text textSize="small" textWeight="500">
                    {label}
                  </Text>
                  <CodeBlock>{`put file.csv ${path}`}</CodeBlock>
                </Div>
              ))}
            </Section>
          </Div>
        </>
      ) : null}
    </>
  );
}

ClientSftpDocs.propTypes = {
  username: PropTypes.string,
};

ClientSftpDocs.defaultProps = {
  username: '',
};

export default ClientSftpDocs;
